import {
	$isCodeNode,
	getCodeLanguages,
	getDefaultCodeLanguage,
} from "@lexical/code";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import {
	$isListNode,
	INSERT_ORDERED_LIST_COMMAND,
	INSERT_UNORDERED_LIST_COMMAND,
	ListNode,
	REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createQuoteNode, $isHeadingNode } from "@lexical/rich-text";
import { $isAtNodeEnd, $wrapNodes } from "@lexical/selection";
import { $getNearestNodeOfType, mergeRegister } from "@lexical/utils";
import {
	$getNodeByKey,
	$getSelection,
	$isRangeSelection,
	FORMAT_TEXT_COMMAND,
	SELECTION_CHANGE_COMMAND,
} from "lexical";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Popper } from 'src/components/Default/LekUI/Popper';
import { Tooltip } from "src/components/Default/LekUI/Tooltip";
import BoldIcon from "src/images/Icons/newIcons/toolbarPlugin/bold.svg";
import CameraIcon from "src/images/Icons/newIcons/toolbarPlugin/camera.svg";
import DotListIcon from "src/images/Icons/newIcons/toolbarPlugin/dotList.svg";
import ItalicIcon from "src/images/Icons/newIcons/toolbarPlugin/italic.svg";
import LinkIcon from "src/images/Icons/newIcons/toolbarPlugin/link.svg";
import NumberListIcon from "src/images/Icons/newIcons/toolbarPlugin/numberList.svg";
import QuotaIcon from "src/images/Icons/newIcons/toolbarPlugin/quota.svg";

import { isUserBanned } from "../../../hooks/useUser";

const LowPriority = 1;
function positionEditorElement(editor, rect) {
	if (rect === null) {
		editor.style.opacity = "0";
		editor.style.top = "-1000px";
		editor.style.left = "-1000px";
	} else {
		editor.style.opacity = "1";
		editor.style.top = `${rect.top + rect.height + window.pageYOffset + 10}px`;
		editor.style.left = `${
			rect.left + window.pageXOffset - editor.offsetWidth / 2 + rect.width / 2
		}px`;
	}
}

function FloatingLinkEditor({ editor }) {
	const editorRef = useRef(null);
	const inputRef = useRef(null);
	const mouseDownRef = useRef(false);
	const [linkUrl, setLinkUrl] = useState("");
	const [isEditMode, setEditMode] = useState(false);
	const [lastSelection, setLastSelection] = useState(null);

	const updateLinkEditor = useCallback(() => {
		const selection = $getSelection();
		if ($isRangeSelection(selection)) {
			const node = getSelectedNode(selection);
			const parent = node.getParent();
			if ($isLinkNode(parent)) {
				setLinkUrl(parent.getURL());
			} else if ($isLinkNode(node)) {
				setLinkUrl(node.getURL());
			} else {
				setLinkUrl("");
			}
		}
		const editorElem = editorRef.current;
		const nativeSelection = window.getSelection();
		const { activeElement } = document;

		if (editorElem === null) {
			return;
		}

		const rootElement = editor.getRootElement();
		if (
			selection !== null &&
			!nativeSelection.isCollapsed &&
			rootElement !== null &&
			rootElement.contains(nativeSelection.anchorNode)
		) {
			const domRange = nativeSelection.getRangeAt(0);
			let rect;
			if (nativeSelection.anchorNode === rootElement) {
				let inner = rootElement;
				while (inner.firstElementChild != null) {
					inner = inner.firstElementChild;
				}
				rect = inner.getBoundingClientRect();
			} else {
				rect = domRange.getBoundingClientRect();
			}

			if (!mouseDownRef.current) {
				positionEditorElement(editorElem, rect);
			}
			setLastSelection(selection);
		} else if (!activeElement || activeElement.className !== "link-input") {
			positionEditorElement(editorElem, null);
			setLastSelection(null);
			setEditMode(false);
			setLinkUrl("");
		}

		return true;
	}, [editor]);

	useEffect(() => {
		return mergeRegister(
			editor.registerUpdateListener(({ editorState }) => {
				editorState.read(() => {
					updateLinkEditor();
				});
			}),

			editor.registerCommand(
				SELECTION_CHANGE_COMMAND,
				() => {
					updateLinkEditor();
					return true;
				},
				LowPriority,
			),
		);
	}, [editor, updateLinkEditor]);

	useEffect(() => {
		editor.getEditorState().read(() => {
			updateLinkEditor();
		});
	}, [editor, updateLinkEditor]);

	useEffect(() => {
		if (isEditMode && inputRef.current) {
			inputRef.current.focus();
		}
	}, [isEditMode]);

	return (
		<div ref={editorRef} className="link-editor">
			{isEditMode ? (
				<input
					ref={inputRef}
					className="link-input"
					value={linkUrl}
					onChange={(event) => {
						setLinkUrl(event.target.value);
					}}
					onKeyDown={(event) => {
						if (event.key === "Enter") {
							event.preventDefault();
							if (lastSelection !== null) {
								if (linkUrl !== "") {
									editor.dispatchCommand(TOGGLE_LINK_COMMAND, linkUrl);
								}
								setEditMode(false);
							}
						} else if (event.key === "Escape") {
							event.preventDefault();
							setEditMode(false);
						}
					}}
				/>
			) : (
				<div className="link-input">
					<a href={linkUrl} target="_blank" rel="noopener noreferrer">
						{linkUrl}
					</a>
					<div
						className="link-edit"
						role="button"
						tabIndex={0}
						onMouseDown={(event) => event.preventDefault()}
						onClick={() => {
							setEditMode(true);
						}}
					/>
				</div>
			)}
		</div>
	);
}

function Select({ onChange, className, options, value }) {
	return (
		<select className={className} onChange={onChange} value={value}>
			<option hidden={true} value="" />
			{options.map((option) => (
				<option key={option} value={option}>
					{option}
				</option>
			))}
		</select>
	);
}

function getSelectedNode(selection) {
	const { anchor } = selection;
	const { focus } = selection;
	const anchorNode = selection.anchor.getNode();
	const focusNode = selection.focus.getNode();
	if (anchorNode === focusNode) {
		return anchorNode;
	}
	const isBackward = selection.isBackward();
	if (isBackward) {
		return $isAtNodeEnd(focus) ? anchorNode : focusNode;
	}
	return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
}

export default function ToolbarPlugin({ openUploadModal, additionalPlugins }) {
	const { t } = useTranslation();
	const [editor] = useLexicalComposerContext();
	const toolbarRef = useRef(null);
	const [blockType, setBlockType] = useState("paragraph");
	const [selectedElementKey, setSelectedElementKey] = useState(null);
	const [codeLanguage, setCodeLanguage] = useState("");
	const [isLink, setIsLink] = useState(false);
	const [isBold, setIsBold] = useState(false);
	const [isItalic, setIsItalic] = useState(false);

	const updateToolbar = useCallback(() => {
		const selection = $getSelection();
		if ($isRangeSelection(selection)) {
			const anchorNode = selection.anchor.getNode();
			const element =
				anchorNode.getKey() === "root"
					? anchorNode
					: anchorNode.getTopLevelElementOrThrow();
			const elementKey = element.getKey();
			const elementDOM = editor.getElementByKey(elementKey);
			if (elementDOM !== null) {
				setSelectedElementKey(elementKey);
				if ($isListNode(element)) {
					const parentList = $getNearestNodeOfType(anchorNode, ListNode);
					const type = parentList ? parentList.getTag() : element.getTag();
					setBlockType(type);
				} else {
					const type = $isHeadingNode(element)
						? element.getTag()
						: element.getType();
					setBlockType(type);
					if ($isCodeNode(element)) {
						setCodeLanguage(element.getLanguage() || getDefaultCodeLanguage());
					}
				}
			}
			// Update text format
			setIsBold(selection.hasFormat("bold"));
			setIsItalic(selection.hasFormat("italic"));

			// Update links
			const node = getSelectedNode(selection);
			const parent = node.getParent();
			if ($isLinkNode(parent) || $isLinkNode(node)) {
				setIsLink(true);
			} else {
				setIsLink(false);
			}
		}
	}, [editor]);

	useEffect(() => {
		return mergeRegister(
			editor.registerUpdateListener(({ editorState }) => {
				editorState.read(() => {
					updateToolbar();
				});
			}),
			editor.registerCommand(
				SELECTION_CHANGE_COMMAND,
				() => {
					updateToolbar();
					return false;
				},
				LowPriority,
			),
		);
	}, [editor, updateToolbar]);

	const codeLanguges = useMemo(() => getCodeLanguages(), []);
	const onCodeLanguageSelect = useCallback(
		(e) => {
			editor.update(() => {
				if (selectedElementKey !== null) {
					const node = $getNodeByKey(selectedElementKey);
					if ($isCodeNode(node)) {
						node.setLanguage(e.target.value);
					}
				}
			});
		},
		[editor, selectedElementKey],
	);

	const insertLink = useCallback(() => {
		if (!isLink) {
			editor.dispatchCommand(TOGGLE_LINK_COMMAND, "https://");
		} else {
			editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
		}
	}, [editor, isLink]);

	const formatBulletList = () => {
		if (blockType !== "ul") {
			editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND);
		} else {
			editor.dispatchCommand(REMOVE_LIST_COMMAND);
		}
	};

	const formatNumberedList = () => {
		if (blockType !== "ol") {
			editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND);
		} else {
			editor.dispatchCommand(REMOVE_LIST_COMMAND);
		}
	};

	const formatQuote = () => {
		if (blockType !== "quote") {
			editor.update(() => {
				const selection = $getSelection();

				if ($isRangeSelection(selection)) {
					$wrapNodes(selection, () => $createQuoteNode());
				}
			});
		}
	};

	return (
		<div className="toolbar" ref={toolbarRef}>
			{blockType === "code" ? (
				<>
					<Select
						className="toolbar-item code-language"
						onChange={onCodeLanguageSelect}
						options={codeLanguges}
						value={codeLanguage}
					/>
					<i className="chevron-down inside" />
				</>
			) : (
				<>
					<button
						type="button"
						onClick={() => {
							editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
						}}
						className={`toolbar-item spaced ${isBold ? "active" : ""}`}
						aria-label="Format Bold"
					>
						<BoldIcon className="icon-super-small icon-black" />
					</button>
					<button
						type="button"
						onClick={() => {
							editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
						}}
						className={`toolbar-item spaced ${isItalic ? "active" : ""}`}
						aria-label="Format Italics"
					>
						<ItalicIcon className="icon-super-small icon-black" />
					</button>
					<button
						type="button"
						onClick={insertLink}
						className={`toolbar-item spaced ${isLink ? "active" : ""}`}
						aria-label="Insert Link"
					>
						<LinkIcon className="icon-super-small icon-black" />
					</button>
					<button
						type="button"
						onClick={formatBulletList}
						className={`toolbar-item spaced ${blockType === "ul" ? "active" : ""}`}
						aria-label="Bullet list"
					>
						<DotListIcon className="icon-super-small icon-black" />
					</button>
					<button
						type="button"
						onClick={formatNumberedList}
						className={`toolbar-item spaced ${blockType === "ol" ? "active" : ""}`}
						aria-label="Numbered List"
					>
						<NumberListIcon className="icon-super-small icon-black" />
					</button>
					<button
						type="button"
						onClick={formatQuote}
						className={`toolbar-item spaced ${blockType === "quote" ? "active" : ""}`}
						aria-label="Quote"
					>
						<QuotaIcon className="icon-super-small icon-black" />
					</button>
					{additionalPlugins}
          {openUploadModal && (
						<>
							<div className="divider divider--vertical" />
							<Tooltip title={t("exam.comments.addImage")}>
								<button
									type="button"
									disabled={!!isUserBanned()}
									onClick={openUploadModal}
									className={"toolbar-item spaced"}
									aria-label="Quote"
								>
									<CameraIcon className="icon-super-small icon-black" />
								</button>
							</Tooltip>
						</>
					)}
					{isLink &&
						createPortal(<FloatingLinkEditor editor={editor} />, document.body)}
				</>
			)}
		</div>
	);
}
